import { useMemo } from 'react'

import {
  useFilters, useModals, useSetState, useNumericParams,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowManagePublicationPaginationRulePayload } from '@hooks/usePublicationPaginationRule'

import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateOrEditPaginationRuleModal from '@modals/CreateOrEditPaginationRuleModal'
import ManagePublicationPaginationRulesModal from '@modals/ManagePublicationPaginationRulesModal'

import PageContent from './components/PageContent'

const defaultState = {
  showBulkCreateBrandProviderModal: false,
  showCreateOrEditPaginationRuleModal: false,
  showManagePublicationPaginationRulesModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditPaginationRuleModal: {
      closeModal: () => setState({ showCreateOrEditPaginationRuleModal: false }),
      updatePaginationRule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManagePublicationPaginationRulesModal: {
      closeModal: () => setState({ showManagePublicationPaginationRulesModal: false }),
      manageAssignedPublications: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateBrandProviderModal,
    showCreateOrEditPaginationRuleModal,
    showManagePublicationPaginationRulesModal,
  } = state

  const { paginationRuleId } = useNumericParams()

  const {
    callbacks: filterCallbacks,
    defaultFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      deletePaginationRule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deletePublicationPaginationRule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateBrandProviderModal: (payload: HandleCallbackActionParams) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCreateOrEditPaginationRuleModal: (payload: object) => {
        setModalData('CreateOrEditPaginationRuleModal', payload)
        setState({ showCreateOrEditPaginationRuleModal: true })
      },
      showManagePublicationPaginationRulesModal: (payload: ShowManagePublicationPaginationRulePayload) => {
        setModalData('ManagePublicationPaginationRulesModal', payload)
        setState({ showManagePublicationPaginationRulesModal: true })
      },
      updatePaginationRule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    defaultFilters,
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent paginationRuleId={paginationRuleId} />

        <BulkCreateBrandProviderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateOrEditPaginationRuleModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditPaginationRuleModal', setState)}
          showModal={showCreateOrEditPaginationRuleModal}
        />

        <ManagePublicationPaginationRulesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManagePublicationPaginationRulesModal', setState)}
          showModal={showManagePublicationPaginationRulesModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
